import {
  XMarkIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useCallback } from 'react';

import { BrandLogo } from '../BrandLogo';
import { SidebarNav } from '../Sidebar';
import { ViewModeDropup } from '../Sidebar';

import type { MobileDrawerProps } from './MobileDrawer.types';

import { useBodyScrollLock } from '@/hooks';
import { LAYOUT_DIMENSIONS } from '@/types/layout';

/**
 * MobileDrawer is a slide-out navigation drawer for mobile devices.
 *
 * Features:
 * - Slides in from the left (280px width, max 80% screen)
 * - Logo/brand in header
 * - Reuses SidebarNav for navigation items
 * - Logout button at bottom
 * - Smooth slide animation (300ms ease-out)
 * - Swipe-to-close gesture
 * - Click-outside to close (via backdrop)
 *
 * @example
 * ```tsx
 * <MobileDrawer
 *   isOpen={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   items={navItems}
 *   activeItemId="dashboard"
 *   onLogout={handleLogout}
 * />
 * ```
 */
export function MobileDrawer({
  isOpen,
  onClose,
  items,
  activeItemId,
  onItemClick,
  onLogout,
  logoutLabel = null,
  logo,
  className = '',
  showViewMode = false,
  viewMode,
  onViewModeChange,
}: MobileDrawerProps): React.JSX.Element {
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchCurrentX = useRef<number | null>(null);
  const isDragging = useRef(false);

  // Handle escape key to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Hold the page's scroll while open — counted with every other overlay's,
  // so closing the drawer never unlocks a page another overlay still holds.
  useBodyScrollLock(isOpen);

  // Handle touch start for swipe gesture
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchCurrentX.current = touch.clientX;
    isDragging.current = true;
  }, []);

  // Handle touch move for swipe gesture
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || touchStartX.current === null) return;

    const touch = e.touches[0];
    if (!touch) return;
    touchCurrentX.current = touch.clientX;
    const deltaX = touchCurrentX.current - touchStartX.current;

    // Only allow swiping left (to close)
    if (deltaX < 0 && drawerRef.current) {
      const translateX = Math.max(deltaX, -LAYOUT_DIMENSIONS.mobileDrawerWidth);
      drawerRef.current.style.transform = `translateX(${translateX}px)`;
      drawerRef.current.style.transition = 'none';
    }
  }, []);

  // Handle touch end for swipe gesture
  const handleTouchEnd = useCallback(() => {
    if (
      !isDragging.current ||
      touchStartX.current === null ||
      touchCurrentX.current === null
    ) {
      return;
    }

    const deltaX = touchCurrentX.current - touchStartX.current;
    const threshold = LAYOUT_DIMENSIONS.mobileDrawerWidth * 0.3;

    if (drawerRef.current) {
      drawerRef.current.style.transition =
        'transform var(--duration-slow) ease-out';

      // Close if swiped more than 30% of drawer width
      if (deltaX < -threshold) {
        drawerRef.current.style.transform = 'translateX(-100%)';
        setTimeout(onClose, 300);
      } else {
        drawerRef.current.style.transform = 'translateX(0)';
      }
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
    isDragging.current = false;
  }, [onClose]);

  // Handle nav item click
  const handleItemClick = useCallback(
    (item: Parameters<NonNullable<typeof onItemClick>>[0]) => {
      onItemClick?.(item);
      onClose();
    },
    [onItemClick, onClose]
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-scrim-strong transition-opacity duration-slow ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`mobile-drawer fixed bottom-0 left-0 top-0 z-50 flex flex-col bg-contrast-700 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-lift-sheet transition-transform duration-slow ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${className}`}
        style={{
          width: LAYOUT_DIMENSIONS.mobileDrawerWidth,
          maxWidth: `${LAYOUT_DIMENSIONS.mobileDrawerMaxWidthPercent}vw`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation drawer"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header with logo and close button */}
        <div className="mobile-drawer__header flex items-center justify-between border-b border-stroke-default px-4 py-3">
          {logo ?? <BrandLogo />}
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 transition-colors duration-fast hover:bg-contrast-600 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
            aria-label="Close navigation drawer"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <div className="mobile-drawer__nav flex-1 overflow-y-auto py-2">
          <SidebarNav
            items={items}
            activeItemId={activeItemId || null}
            onItemClick={handleItemClick}
            collapsed={false}
          />
        </div>

        {/* View mode dropup (dashboard only) */}
        {showViewMode && (
          <div className="mobile-drawer__view-mode border-t border-stroke-default px-4 py-3">
            <ViewModeDropup
              value={viewMode}
              onChange={onViewModeChange ?? (() => {})}
              collapsed={false}
            />
          </div>
        )}

        {/* Footer with logout */}
        {onLogout && (
          <div className="mobile-drawer__footer border-t border-stroke-default px-4 py-3">
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-neutral-400 transition-colors duration-fast hover:bg-contrast-600 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-state-focus"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span className="text-label-sm font-medium">
                {logoutLabel ?? 'Logout'}
              </span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

export default MobileDrawer;
